<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SvgIcon from '@/presentation/components/common/svg-icon/SvgIcon.vue';
import CopilotBox from '@/presentation/components/copilot-box/CopilotBox.vue';
import SettingLevel from '@/presentation/pages/ai-disclosure/components/SettingLevel.vue';
import ConfirmDialog from '@/presentation/components/common/confirm-dialog/ConfirmDialog.vue';
import '@patsnap-ui/icon/assets/solid/settings.svg';
import { useAiDisclosureResult } from '@/composables/use-ai-disclosure-result';
import { useAiDisclosure } from '@/composables/use-ai-disclosure';
import type { GoalSettingWithRecommend } from '@/services/ai-disclosure/type/ai-disclosure.type';
import '@patsnap-ui/icon/assets/solid/DoubleArrowDown.svg';
import '@patsnap-ui/icon/assets/solid/DoubleArrowUp.svg';
import _ from 'lodash';
import RecommendSelection from '@/presentation/pages/ai-disclosure/components/RecommendSelection.vue';

const { t } = useI18n();
const {
    aiDisclosureResultService,
    aiDisclosureResultData,
} = useAiDisclosureResult();

const { aiDisclosureData } = useAiDisclosure();

const showSettingDialog = ref<boolean>(false);
const cacheSettings = ref<GoalSettingWithRecommend>(_.cloneDeep(aiDisclosureResultData.value.settings));
const collapseOriginInput = ref<boolean>(true);

const settings = computed(() => aiDisclosureResultData.value.settings);
const cacheRecommendInventionSubject = computed(() => {
    return cacheSettings.value.recommendInventionSubject.filter(
        (item) => !cacheSettings.value.inventionSubject.includes(item)
    );
});
const cacheRecommendApplicationField = computed(() => {
    return cacheSettings.value.recommendApplicationField.filter(
        (item) => !cacheSettings.value.applicationField.includes(item)
    );
});

const emit = defineEmits<{
    (e: 'submit'): void;
}>();

function handleSetting() {
    showSettingDialog.value = true;
}

const isSubmitting = ref<boolean>(false);

const isConfirmDisabled = computed(() => {
    return !cacheSettings.value.techMeans.length || isSubmitting.value;
});

async function settingsDialogConfirm() {
    if (isSubmitting.value) {
        return;
    }

    isSubmitting.value = true;
    try {
        await aiDisclosureResultService.updateSetting(cacheSettings.value, false);
        emit('submit');
        showSettingDialog.value = false;
        aiDisclosureResultService.generateResult();
    } finally {
        isSubmitting.value = false;
    }
}

function settingsDialogCancel() {
    showSettingDialog.value = false;
    cacheSettings.value = _.cloneDeep(aiDisclosureResultData.value.settings);
}

function toggleCollapseOriginInput() {
    collapseOriginInput.value = !collapseOriginInput.value;
}
</script>

<template>
    <div>
        <CopilotBox>
            <div
                class="disclosure-setting h-full overflow-y-hidden"
            >
                <div class="flex items-center justify-between">
                    <span class="font-semibold">
                        {{ t('aiDisclosure.pointContent') }}
                    </span>
                    <button
                        class="th-btn th-btn-plain flex cursor-pointer items-center text-primary"
                        @click="handleSetting"
                    >
                        <SvgIcon
                            class="th-btn-icon h-6 w-6 !text-primary"
                            icon="SolidSettings"
                        />
                        <span>
                            {{ t('aiDisclosure.adjustment') }}
                        </span>
                    </button>
                </div>

                <div class="disclosure-setting-content">
                    <template v-if="settings.inventionSubject.length">
                        <div class="disclosure-setting-item__title pt-4 text-xs text-grey-90">
                            {{ t('aiDisclosure.inventionSubject') }}
                        </div>
                        <div class="flex flex-wrap">
                            <div
                                v-for="(item,idx) in settings.inventionSubject"
                                :key="idx"
                                class="mr-1 mt-2 rounded bg-grey-20 p-1 text-xs text-black"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </template>

                    <template v-if="settings.applicationField.length">
                        <div class="disclosure-setting-item__title pt-4 text-xs text-grey-90">
                            {{ t('aiDisclosure.applicationField') }}
                        </div>
                        <div class="flex flex-wrap">
                            <div
                                v-for="(item,idx) in settings.applicationField"
                                :key="idx"
                                class="mr-1 mt-2 rounded bg-grey-20 p-1 text-xs text-black"
                            >
                                {{ item }}
                            </div>
                        </div>
                    </template>

                    <template v-if="settings.techMeans">
                        <div class="disclosure-setting-item__title pt-4 text-xs text-grey-90">
                            {{ t('aiDisclosure.techMeans') }}
                        </div>
                        <div class="mr-1 mt-2 text-xs text-black">
                            {{ settings.techMeans }}
                        </div>
                    </template>

                    <template v-if="settings.techEffect">
                        <div class="disclosure-setting-item__title pt-4 text-xs text-grey-90">
                            {{ t('aiDisclosure.techEffect') }}
                        </div>
                        <div class="mr-1 mt-2 text-xs text-black">
                            {{ settings.techEffect }}
                        </div>
                    </template>

                    <template v-if="settings.other">
                        <div class="disclosure-setting-item__title pt-4 text-xs text-grey-90">
                            {{ t('aiDisclosure.other') }}
                        </div>
                        <div class="mr-1 mt-2 text-xs text-black">
                            {{ settings.other }}
                        </div>
                    </template>
                </div>

                <SettingLevel
                    :settings="settings"
                    class="mt-4"
                />
            </div>
        </CopilotBox>

        <ConfirmDialog
            v-if="showSettingDialog"
            class="input-tech-means-dialog"
            :width="800"
            :title="t('aiDisclosure.AIAssistant')"
            :confirmText="t('aiDisclosureGlobal.globalBtn.regenerate')"
            :disable="isConfirmDisabled"
            data-theme="patsnapRD"
            @cancel="settingsDialogCancel"
            @confirm="settingsDialogConfirm"
        >
            <div class="h-full overflow-hidden">
                <div class="relative flex rounded bg-grey-10 p-1">
                    <div
                        class="mr-1 h-6 shrink-0 rounded bg-grey-20 px-4 py-1 text-xs text-grey-450"
                    >
                        {{ t('aiDisclosure.originalLabel') }}
                    </div>

                    <div
                        class="text-sm !leading-6 text-grey-450"
                        :class="[collapseOriginInput?'line-clamp-1':'']"
                    >
                        {{ aiDisclosureData.input }}
                    </div>

                    <div class="goals-setting-collapse-btn absolute bottom-1 right-1 h-6">
                        <button
                            class="th-btn-small th-btn th-btn-plain !h-6 !min-h-0 bg-grey-10 p-1 pl-2 text-xs text-blue-500"
                            @click="toggleCollapseOriginInput"
                        >
                            <SvgIcon
                                class="th-btn-icon mr-1 h-4 w-4 !text-blue-500"
                                :icon="collapseOriginInput? 'SolidDoubleArrowDown':'SolidDoubleArrowUp'"
                            />

                            {{
                                collapseOriginInput ? t('aiDisclosureGlobal.globalBtn.expand') : t('aiDisclosureGlobal.globalBtn.collapse')
                            }}
                        </button>
                    </div>
                </div>

                <div class="mt-4 border-t border-grey-40 pt-4 text-sm font-semibold text-black">
                    {{ t('aiDisclosure.pointContent') }}
                </div>
                <SettingLevel :settings="cacheSettings" class="mt-4" />

                <div class="disclosure-setting-item__title mb-1 pt-4 text-xs text-grey-90">
                    {{ t('aiDisclosure.inventionSubject') }}
                </div>

                <RecommendSelection
                    :placeholder="t('aiDisclosure.improvedSubjectPlaceholder')"
                    :list="cacheSettings.inventionSubject"
                    :options="cacheRecommendInventionSubject"
                    @change="value => cacheSettings.inventionSubject = value"
                />

                <div class="disclosure-setting-item__title mb-1 pt-4 text-xs text-grey-90">
                    {{ t('aiDisclosure.applicationField') }}
                </div>

                <RecommendSelection
                    :placeholder="t('aiDisclosure.usagePlaceholder')"
                    :list="cacheSettings.applicationField"
                    :options="cacheRecommendApplicationField"
                    @change="value => cacheSettings.applicationField = value"
                />

                <div class="disclosure-setting-item__title mb-1 pt-4 text-xs text-grey-90">
                    {{ t('aiDisclosure.techMeans') }}
                    <span class="text-xs text-red-500">*</span>
                </div>

                <textarea
                    v-model="cacheSettings.techMeans"
                    class="h-[72px] w-full resize-none rounded border border-grey-55 px-3 py-1.5 text-black"
                    :placeholder="t('aiDisclosure.technicalMeansPlaceholder')"
                />

                <div class="disclosure-setting-item__title mb-1 pt-4 text-xs text-grey-90">
                    {{ t('aiDisclosure.techEffect') }}
                </div>

                <textarea
                    v-model="cacheSettings.techEffect"
                    class="h-[72px] w-full resize-none rounded border border-grey-55 px-3 py-1.5 text-black"
                    :placeholder="t('aiDisclosure.technicalBenefitsPlaceholder')"
                />

                <div class="disclosure-setting-item__title mb-1 pt-4 text-xs text-grey-90">
                    {{ t('aiDisclosure.other') }}
                </div>

                <textarea
                    v-model="cacheSettings.other"
                    class="h-[72px] w-full resize-none rounded border border-grey-55 px-3 py-1.5 text-black"
                    :placeholder="t('aiDisclosure.othersPlaceholder')"
                />
            </div>
        </ConfirmDialog>
    </div>
</template>

<style lang="scss">
.input-tech-means-dialog {
    .goals-setting-collapse-btn {
        background: rgb(250 251 252);
    }
}

</style>
